import { useEffect, useRef } from 'react';
import { getApiBaseUrl } from '../utils/chatIdentity';

const API_BASE_URL = getApiBaseUrl();
const FALLBACK_POLL_MS = 60000;
const EVENT_DEBOUNCE_MS = 300;

// Replaces polling for partner-request/relay-status changes with a push-driven refresh:
// subscribes to the backend's per-diagnostic-ticket SSE stream (scripts/agent_proxy.py's
// `partner_repair_requests_stream`) and calls `onUpdate` whenever something changed, instead
// of re-fetching on a fixed interval regardless of whether anything actually happened. The
// event payload itself is never trusted as the new state — every event just triggers the
// caller's existing fetch, so the caller's own request/response shape doesn't change.
export default function usePartnerRequestUpdates({ diagnosticTicketId, isAuthenticated, onUpdate }) {
    const onUpdateRef = useRef(onUpdate);
    onUpdateRef.current = onUpdate;

    useEffect(() => {
        if (!isAuthenticated || !diagnosticTicketId) return undefined;

        let debounceTimer = null;
        const refresh = () => onUpdateRef.current?.();
        const debouncedRefresh = () => {
            window.clearTimeout(debounceTimer);
            debounceTimer = window.setTimeout(refresh, EVENT_DEBOUNCE_MS);
        };

        refresh();

        const streamUrl = `${API_BASE_URL}/api/partner-repair-requests/stream?diagnostic_ticket_id=${encodeURIComponent(diagnosticTicketId)}`;
        const eventSource = new EventSource(streamUrl, { withCredentials: true });
        // A fresh connection (first open, or a reconnect after a drop) may have missed
        // events while it wasn't listening — refetch on every open, not just the first one,
        // so a dropped connection can't leave the UI silently stale.
        eventSource.onopen = refresh;
        eventSource.addEventListener('update', debouncedRefresh);

        // Safety net only, not the primary channel — guards a connection that looks open
        // but has silently stalled (e.g. a proxy that swallows the stream without closing it).
        const fallbackIntervalId = window.setInterval(refresh, FALLBACK_POLL_MS);

        return () => {
            eventSource.close();
            window.clearTimeout(debounceTimer);
            window.clearInterval(fallbackIntervalId);
        };
    }, [isAuthenticated, diagnosticTicketId]);
}
